/**
 * @fileoverview Basic Info Tab for Institution Central
 * Displays institution basic information in a simple list format
 */

import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Chip, Divider, Button, Stack } from "@mui/material";
import { intl, DmsPermissions } from "dmsbasereact";
import { Permissions } from "../../../constants/Permissions";
import EditIcon from "@mui/icons-material/Edit";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import PersonIcon from "@mui/icons-material/Person";

import { getInstitutionCategoryLabel } from "../../../constants/InstitutionType";
import { InstitutionStatusEnum, InstitutionStatus } from "../../../constants/InstitutionStatus";
import { useFormatters } from "../../../hooks/useFormater";
import { formatCep } from "../../../utils/formatters";

/**
 * InfoRow - Simple row with icon and value
 */
const InfoRow = ({ icon, label, value }) => {
    if (!value) return null;
    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1 }}>
            <Box sx={{ color: "text.secondary", mt: 0.3 }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                    {label}
                </Typography>
                <Typography variant="body2">
                    {value}
                </Typography>
            </Box>
        </Box>
    );
};

InfoRow.propTypes = {
    icon: PropTypes.node.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string
};

/**
 * BasicInfoTab - Displays institution basic information
 */
export const BasicInfoTab = ({ institution = null,
  onEdit = null}) => {
    const orgBranch = institution?.organization_branch;
    const address = orgBranch?.address;
    const statusCode = institution?.status;
    const statusLabel = InstitutionStatus.getText(statusCode);
    const statusColor = InstitutionStatus.getColor(statusCode);
    const categoryLabel = getInstitutionCategoryLabel(orgBranch?.category);
    const isApproved = statusCode === InstitutionStatusEnum.APPROVED;
    const formatters = useFormatters();

    const formatAddress = () => {
        if (!address) return null;
        const parts = [];
        if (address.address) parts.push(address.address);
        if (address.number) parts.push(address.number);
        if (address.city) parts.push(address.city);
        if (address.state) parts.push(address.state);
        if (address.zip_code) parts.push(`CEP: ${formatCep(address.zip_code)}`);
        return parts.join(", ");
    };

    return (
        <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Chip
                    icon={<BusinessIcon />}
                    label={statusLabel}
                    color={statusColor}
                    size="small"
                />
                {institution?.user_account_status != null && institution.user_account_status_label && (
                    <Chip
                        icon={<PersonIcon />}
                        label={institution.user_account_status_label}
                        size="small"
                        variant="outlined"
                        color={
                            institution.user_account_status === 5 ? "success"
                            : institution.user_account_status === 6 ? "error"
                            : institution.user_account_status === 7 ? "error"
                            : "warning"
                        }
                    />
                )}
                {categoryLabel && (
                    <Chip
                        label={categoryLabel}
                        variant="outlined"
                        size="small"
                    />
                )}
            </Stack>

            <Divider sx={{ my: 2 }} />

            {statusCode === InstitutionStatusEnum.REJECTED && institution?.status_justification && (
                <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                   <Typography variant="caption" color="text.secondary" fontWeight="medium">
                        {intl.get("RejectionReason").d("Rejection Reason")}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {institution.status_justification}
                    </Typography>
                </Box>
            )}

            {statusCode === InstitutionStatusEnum.INACTIVE && institution?.status_justification && (
                <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                        {intl.get("InactivationReason").d("Inactivation Reason")}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {institution.status_justification}
                    </Typography>
                </Box>
            )}

            <InfoRow
                icon={<BusinessIcon fontSize="small" />}
                label={intl.get("InstitutionName").d("Institution Name")}
                value={orgBranch?.name}
            />

            <InfoRow
                icon={<BadgeIcon fontSize="small" />}
                label={intl.get("CNPJ").d("CNPJ")}
                value={ formatters.formatCNPJ(orgBranch?.cnpj)  }
            />

            <InfoRow
                icon={<LocationOnIcon fontSize="small" />}
                label={intl.get("Address").d("Address")}
                value={formatAddress()}
            />

            <InfoRow
                icon={<PhoneIcon fontSize="small" />}
                label={intl.get("Phone").d("Phone")}
                value={orgBranch?.phone}
            />

            <InfoRow
                icon={<EmailIcon fontSize="small" />}
                label={intl.get("Email").d("Email")}
                value={orgBranch?.email}
            />


            {onEdit && (
                <DmsPermissions permissions={[Permissions.INSTITUTION_EDIT]}>
                    <Box sx={{ mt: 3 }}>
                        <Button
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={onEdit}
                            fullWidth
                        >
                            {intl.get("EditInformation").d("Edit Information")}
                        </Button>
                    </Box>
                </DmsPermissions>
            )}
        </Box>
    );
};

BasicInfoTab.propTypes = {
    institution: PropTypes.object,
    onEdit: PropTypes.func
};

